import { Client } from '@stomp/stompjs';
import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { getPageId } from '@/services/utl-service';
import { clearAllTopicHandlers } from '@/lib/websocket-topic-registry';

type WebSocketContextValue = {
  client: Client | null;
  connected: boolean;
};

const WebSocketContext = createContext<WebSocketContextValue>({
  client: null,
  connected: false,
});

interface WebSocketProviderProps {
  baseUrl: string;
  token?: string | null;
  children: ReactNode;
  onConnected?: () => void;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  baseUrl,
  token,
  children,
  onConnected,
}) => {
  const [connected, setConnected] = useState(false);
  const clientRef = useRef<Client | null>(null);
  const activatedRef = useRef(false);
  const pageId = getPageId();

  useEffect(() => {
    if (activatedRef.current) return;
    activatedRef.current = true;

    const wsUrl = `${baseUrl.replace(/^http(s?):\/\//, 'ws$1://').replace(/\/+$/, '')}/ws`;

    const client = new Client({
      brokerURL: wsUrl,
      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      connectHeaders: token ? { Authorization: `Bearer ${token}` } : {},
      debug: (str) => console.log('[STOMP]', str),

      onConnect: () => {
        console.log('STOMP connected -->', onConnected);
        setConnected(true);
        onConnected?.();
      },

      onDisconnect: () => {
        console.log('STOMP disconnected');
        // Flush the module-level topic registry so stale StompSubscription
        // objects from this connection are not reused after reconnect.
        clearAllTopicHandlers();
        setConnected(false);
      },

      onStompError: (frame) => {
        console.error('STOMP error', frame.headers['message'], frame.body);
      },

      onWebSocketClose: (e) => {
        console.warn('WS closed', e);
        // Also flush here — the TCP connection may drop without a clean STOMP
        // DISCONNECT frame, bypassing onDisconnect.
        clearAllTopicHandlers();
        setConnected(false);
      },
    });

    clientRef.current = client;
    client.activate();

    return () => {
      try {
        clientRef.current?.deactivate();
      } catch (e) {
        console.warn('deactivate failed', e);
      }

      clientRef.current = null;
      activatedRef.current = false;
      setConnected(false);
    };
  }, [baseUrl, token, pageId]);

  return (
    <WebSocketContext.Provider value={{ client: clientRef.current, connected }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketClient = () => useContext(WebSocketContext);
